import { Typography } from '@/components/atoms';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import type { GuestPropertyTypeTag } from '../types/guest.types';

const TAG_GAP = 8;
/** Fallback when overflow chip has not measured yet (`+9` / `+99` size). */
const OVERFLOW_WIDTH_FALLBACK = 48;

export interface GuestUnitTypeTagsRowProps {
  readonly tags: ReadonlyArray<GuestPropertyTypeTag>;
  readonly tagBackgroundColor: string;
}

/**
 * How many leading tags fit in one row, reserving space for a `+N` chip
 * when any tags would be omitted.
 */
function computeVisibleUnitTypeCount(
  rowWidth: number,
  tagWidths: readonly number[],
  overflowChipWidth: number,
  gap: number = TAG_GAP,
): number {
  const total = tagWidths.length;
  if (rowWidth <= 0 || total === 0) {
    return 0;
  }

  let allWidth = 0;
  for (let i = 0; i < total; i += 1) {
    allWidth += (i > 0 ? gap : 0) + (tagWidths[i] ?? 0);
  }
  if (allWidth <= rowWidth) {
    return total;
  }

  const overflowW = Math.max(overflowChipWidth, OVERFLOW_WIDTH_FALLBACK);
  let used = 0;
  let visible = 0;
  for (let i = 0; i < total; i += 1) {
    const chip = tagWidths[i] ?? 0;
    const remainingAfter = total - (i + 1);
    const gapBefore = visible > 0 ? gap : 0;
    const nextUsed = used + gapBefore + chip;
    const withOverflow =
      nextUsed + (remainingAfter > 0 ? gap + overflowW : 0);
    if (withOverflow > rowWidth) {
      break;
    }
    used = nextUsed;
    visible = i + 1;
  }
  return visible;
}

/**
 * Listing-only unit-type chips: hug label + padding, single row.
 * Overflow shows as `+N` (never wraps to a second line).
 */
export function GuestUnitTypeTagsRow({
  tags,
  tagBackgroundColor,
}: Readonly<GuestUnitTypeTagsRowProps>) {
  const [rowWidth, setRowWidth] = useState(0);
  const [tagWidths, setTagWidths] = useState<ReadonlyArray<number>>([]);
  const [overflowChipWidth, setOverflowChipWidth] = useState(0);

  const tagKey = useMemo(
    () => tags.map((tag) => tag.id).join('|'),
    [tags],
  );

  useEffect(() => {
    setTagWidths([]);
    setOverflowChipWidth(0);
  }, [tagKey]);

  const onRowLayout = useCallback((event: LayoutChangeEvent) => {
    const next = event.nativeEvent.layout.width;
    setRowWidth((prev) => (prev === next ? prev : next));
  }, []);

  const onTagLayout = useCallback(
    (index: number, width: number) => {
      if (width <= 0) {
        return;
      }
      setTagWidths((prev) => {
        if (prev[index] === width) {
          return prev;
        }
        const next = prev.slice();
        while (next.length < tags.length) {
          next.push(0);
        }
        next[index] = width;
        return next;
      });
    },
    [tags.length],
  );

  const onOverflowLayout = useCallback((width: number) => {
    if (width <= 0) {
      return;
    }
    setOverflowChipWidth((prev) => (prev === width ? prev : width));
  }, []);

  const tagsMeasured =
    tagWidths.length === tags.length && tagWidths.every((w) => w > 0);
  const measuredReady = rowWidth > 0 && tagsMeasured;

  const visibleCount = useMemo(() => {
    if (!measuredReady) {
      // Prefer showing at least one tag + overflow until measure completes,
      // so cards never look like a clipped multi-tag row without `+N`.
      return Math.min(1, tags.length);
    }
    return computeVisibleUnitTypeCount(
      rowWidth,
      tagWidths,
      overflowChipWidth || OVERFLOW_WIDTH_FALLBACK,
    );
  }, [
    measuredReady,
    rowWidth,
    tagWidths,
    overflowChipWidth,
    tags.length,
  ]);

  const visibleTags = tags.slice(0, Math.max(visibleCount, 0));
  const overflowCount = Math.max(0, tags.length - visibleTags.length);
  /** Worst-case label for measuring overflow chip width. */
  const overflowMeasureLabel = `+${Math.max(tags.length - 1, 1)}`;

  if (tags.length === 0) {
    return null;
  }

  const tagStyle = [styles.tag, { backgroundColor: tagBackgroundColor }];

  return (
    <View style={styles.slot} onLayout={onRowLayout}>
      {/*
        Measure off-screen in a column so each chip gets its intrinsic width
        (row measure can under-report on RN Web).
      */}
      <View
        style={styles.measureLayer}
        pointerEvents="none"
        accessibilityElementsHidden
      >
        {tags.map((tag, index) => (
          <View
            key={`measure-${tag.id}`}
            onLayout={(e) => onTagLayout(index, e.nativeEvent.layout.width)}
            style={tagStyle}
          >
            <Typography
              variant="subhead"
              color="primary"
              numberOfLines={1}
              style={styles.tagText}
            >
              {tag.label}
            </Typography>
          </View>
        ))}
        <View
          onLayout={(e) => onOverflowLayout(e.nativeEvent.layout.width)}
          style={tagStyle}
        >
          <Typography variant="subhead" color="primary" style={styles.tagText}>
            {overflowMeasureLabel}
          </Typography>
        </View>
      </View>

      <View style={styles.row}>
        {visibleTags.map((tag) => (
          <View key={tag.id} style={tagStyle}>
            <Typography
              variant="subhead"
              color="primary"
              numberOfLines={1}
              style={styles.tagText}
            >
              {tag.label}
            </Typography>
          </View>
        ))}
        {overflowCount > 0 ? (
          <View
            style={tagStyle}
            accessibilityLabel={`Plus ${overflowCount} more`}
          >
            <Typography
              variant="subhead"
              color="primary"
              style={styles.tagText}
            >
              {`+${overflowCount}`}
            </Typography>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  slot: {
    width: '100%',
    minWidth: 0,
    marginTop: 'auto',
    position: 'relative',
  },
  measureLayer: {
    position: 'absolute',
    left: -9999,
    top: 0,
    opacity: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: TAG_GAP,
    zIndex: -1,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: TAG_GAP,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  /** Hug label width + Spacing/3 horizontal / Spacing/1.5 vertical. */
  tag: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  tagText: {
    letterSpacing: 0,
    textAlign: 'center',
  },
});

export default GuestUnitTypeTagsRow;
